import React, { useState } from "react";
import { YStack, XStack, SizableText, H3, H4, Card, Button, Spinner } from "tamagui";
import apis from "@api/api";
import * as Animatable from 'react-native-animatable';
import { View } from "react-native";
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import ErrorBoundary from "@utils/ErrorBoundary";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { useDispatch } from "react-redux";
import { SHOW_AUTH_MODAL, HIDE_LOADING_MODAL, SHOW_LOADING_MODAL } from "@reduxLocal/actions/types";
import { useQueryClient } from "@tanstack/react-query";

const ShopifyOptions = () => {
  const theme = themeSelector();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncedDataModelId, setSyncedDataModelId] = useState<string | null>(null);

  const handleSyncProducts = async () => {
    try {
      setIsSyncing(true);
      setSyncError(null);
      setSyncSuccess(false);
      dispatch({ type: SHOW_LOADING_MODAL });

      // Call API to sync Shopify products and create data model
      // This endpoint should:
      // 1. Fetch products from Shopify using the authenticated token
      // 2. Create a new data model with tag "products"
      // 3. Add all products as data items
      const response = await apis.syncShopifyProducts();

      if (response && response.dataModelId) {
        setSyncedDataModelId(response.dataModelId);
        setSyncSuccess(true);
        dispatch({ type: HIDE_LOADING_MODAL });
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: `Successfully synced ${response.productCount || 0} products from Shopify!`,
            type: 'success'
          }
        });

        // Invalidate data queries to refresh the data models list
        queryClient.invalidateQueries({ queryKey: ['dataFolders'] });
        queryClient.invalidateQueries({ queryKey: ['dataModels'] });
      } else {
        throw new Error('Invalid response from sync');
      }
    } catch (error: any) {
      console.error('Error syncing Shopify products:', error);
      setSyncError(error.message || 'Failed to sync products from Shopify');
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: `Failed to sync products: ${error.message || 'Unknown error'}`,
          type: 'error'
        }
      });
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <ErrorBoundary>
      <YStack gap="$4">
        {/* Main Sync Card */}
        <Animatable.View animation="fadeInUp" duration={600}>
          <Card
            backgroundColor={theme ? "#ffffff" : "#1e293b"}
            borderColor={theme ? "#e2e8f0" : "#334155"}
            borderRadius={16}
            padding="$5"
            elevate
          >
            <YStack gap="$4">
              {/* Header */}
              <XStack alignItems="center" gap="$3">
                <View style={{
                  backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
                  borderRadius: 12,
                  padding: 12
                }}>
                  <MaterialCommunityIcons
                    name="store"
                    size={32}
                    color="#95bf47"
                  />
                </View>
                <YStack flex={1}>
                  <H3
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                  >
                    Sync Shopify Products
                  </H3>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                  >
                    Import your Shopify products into a new data model
                  </SizableText>
                </YStack>
              </XStack>

              {/* Description */}
              <SizableText
                size="$3"
                color={theme ? "#475569" : "#cbd5e1"}
                lineHeight={20}
              >
                Sync all products from your Shopify store into a new data model tagged with "products".
                This will create a structured dataset that you can use for inventory management, analytics, and more.
              </SizableText>

              {/* Sync Button */}
              {!syncSuccess && (
                <Button
                  backgroundColor="#95bf47"
                  color="white"
                  onPress={handleSyncProducts}
                  disabled={isSyncing}
                  borderRadius={12}
                  paddingVertical="$4"
                  opacity={isSyncing ? 0.6 : 1}
                >
                  {isSyncing ? (
                    <XStack alignItems="center" gap="$2">
                      <Spinner size="small" color="white" />
                      <SizableText color="white" fontWeight="600">
                        Syncing Products...
                      </SizableText>
                    </XStack>
                  ) : (
                    <XStack alignItems="center" gap="$2">
                      <MaterialCommunityIcons name="sync" size={20} color="white" />
                      <SizableText color="white" fontWeight="600">
                        Sync Products Now
                      </SizableText>
                    </XStack>
                  )}
                </Button>
              )}

              {/* Error State */}
              {syncError && !isSyncing && (
                <Animatable.View animation="fadeIn" duration={300}>
                  <Card
                    backgroundColor={theme ? "#fef2f2" : "#450a0a"}
                    borderColor={theme ? "#fecaca" : "#7f1d1d"}
                    borderRadius={12}
                    padding="$4"
                  >
                    <XStack alignItems="center" gap="$3">
                      <MaterialIcons name="error-outline" size={24} color="#ef4444" />
                      <YStack flex={1}>
                        <SizableText
                          size="$3"
                          color="#ef4444"
                          fontWeight="600"
                        >
                          Sync Failed
                        </SizableText>
                        <SizableText
                          size="$2"
                          color={theme ? "#991b1b" : "#fecaca"}
                        >
                          {syncError}
                        </SizableText>
                      </YStack>
                      <Button
                        backgroundColor="#ef4444"
                        color="white"
                        size="$2"
                        onPress={() => {
                          setSyncError(null);
                          setSyncSuccess(false);
                        }}
                        borderRadius={8}
                        paddingHorizontal="$3"
                      >
                        Dismiss
                      </Button>
                    </XStack>
                  </Card>
                </Animatable.View>
              )}
            </YStack>
          </Card>
        </Animatable.View>

        {/* Success State */}
        {syncSuccess && (
          <Animatable.View animation="fadeInUp" duration={600} delay={100}>
            <Card
              backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
              borderColor={theme ? "#dcfce7" : "#065f46"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <YStack alignItems="center" gap="$3">
                <View style={{
                  backgroundColor: theme ? "#d1fae5" : "#065f46",
                  borderRadius: 50,
                  padding: 16
                }}>
                  <MaterialIcons name="check-circle" size={48} color={theme ? "#10b981" : "#34d399"} />
                </View>
                <H4
                  color={theme ? "#059669" : "#10b981"}
                  fontWeight="600"
                  textAlign="center"
                >
                  Products Synced Successfully!
                </H4>
                <SizableText
                  size="$4"
                  color={theme ? "#065f46" : "#34d399"}
                  textAlign="center"
                >
                  Your Shopify products have been imported into a new data model tagged with "products".
                  You can now view and manage them in your Data section.
                </SizableText>
                {syncedDataModelId && (
                  <Button
                    backgroundColor="#10b981"
                    color="white"
                    onPress={() => {
                      // Navigate to the data model (this would need navigation implementation)
                      console.log('Navigate to data model:', syncedDataModelId);
                    }}
                    borderRadius={12}
                    paddingHorizontal="$5"
                    paddingVertical="$3"
                  >
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons name="visibility" size={20} color="white" />
                      <SizableText color="white" fontWeight="600">
                        View Data Model
                      </SizableText>
                    </XStack>
                  </Button>
                )}
                <Button
                  backgroundColor={theme ? "#e2e8f0" : "#334155"}
                  color={theme ? "#1e293b" : "#f1f5f9"}
                  onPress={() => {
                    setSyncSuccess(false);
                    setSyncError(null);
                    setSyncedDataModelId(null);
                  }}
                  borderRadius={12}
                  paddingHorizontal="$5"
                  paddingVertical="$3"
                >
                  <SizableText fontWeight="600">
                    Sync Again
                  </SizableText>
                </Button>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Info Card */}
        <Animatable.View animation="fadeInUp" duration={600} delay={200}>
          <Card
            backgroundColor={theme ? "#f8fafc" : "#1e293b"}
            borderColor={theme ? "#e2e8f0" : "#334155"}
            borderRadius={16}
            padding="$4"
          >
            <XStack alignItems="flex-start" gap="$3">
              <View style={{
                backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
                borderRadius: 8,
                padding: 8
              }}>
                <MaterialIcons name="info" size={20} color="#3b82f6" />
              </View>
              <YStack flex={1} gap="$2">
                <SizableText
                  size="$3"
                  color={theme ? "#1e293b" : "#f1f5f9"}
                  fontWeight="600"
                >
                  What gets synced?
                </SizableText>
                <SizableText
                  size="$2"
                  color={theme ? "#64748b" : "#94a3b8"}
                  lineHeight={18}
                >
                  • Product title, description, and SKU{'\n'}
                  • Product variants and pricing{'\n'}
                  • Inventory quantities{'\n'}
                  • Product images and metadata{'\n'}
                  • Product tags and categories
                </SizableText>
              </YStack>
            </XStack>
          </Card>
        </Animatable.View>
      </YStack>
    </ErrorBoundary>
  );
};

export default ShopifyOptions;

